// Vehicle Filter Schemas - Configuration for all vehicle types
// Each schema defines filters by level: primary, secondary, smart, advanced, auto

export const VEHICLE_TYPES = [
  { slug: 'cars', name: 'Cars', icon: '🚗' },
  { slug: 'vans', name: 'Vans', icon: '🚐' },
  { slug: 'buses', name: 'Buses', icon: '🚌' },
  { slug: 'motorcycles-scooters', name: 'Motorcycles & Scooters', icon: '🏍️' },
  { slug: 'electric-bikes', name: 'Electric Bikes', icon: '🚲' },
  { slug: 'trucks-lorries', name: 'Trucks & Lorries', icon: '🚚' },
  { slug: 'farm-vehicles', name: 'Farm Vehicles', icon: '🚜' },
  { slug: 'plant-construction', name: 'Plant/Construction Equipment', icon: '🏗️' },
  { slug: 'motorhomes', name: 'Motorhomes', icon: '🏠' },
  { slug: 'caravans', name: 'Caravans', icon: '🏕️' },
  { slug: 'boats-watercraft', name: 'Boats & Watercraft', icon: '🚤' },
  { slug: 'three-wheelers', name: 'Three-Wheelers', icon: '🛺' }
];

// Field definitions with types, labels, and options
export const FIELD_DEFINITIONS = {
  // Primary Fields
  make: {
    type: 'select',
    label: 'Make',
    placeholder: 'Any Make',
    searchable: true
  },
  model: {
    type: 'select',
    label: 'Model',
    placeholder: 'Any Model',
    dependsOn: 'make',
    searchable: true
  },
  priceRange: {
    type: 'range',
    label: 'Budget',
    unit: 'Rs',
    min: 0,
    max: 50000000,
    step: 100000
  },
  year: {
    type: 'range',
    label: 'Year',
    min: 1990,
    max: new Date().getFullYear(),
    step: 1
  },
  mileage: {
    type: 'range',
    label: 'Mileage',
    unit: 'km',
    min: 0,
    max: 500000,
    step: 10000
  },
  location: {
    type: 'location',
    label: 'Location',
    placeholder: 'Any Location'
  },
  fuelType: {
    type: 'multiselect',
    label: 'Fuel Type',
    options: ['Petrol', 'Diesel', 'Hybrid', 'Electric', 'CNG', 'LPG']
  },
  condition: {
    type: 'select',
    label: 'Condition',
    options: ['Brand New', 'Reconditioned', 'Used - Excellent', 'Used - Good', 'Used - Fair']
  },
  
  // Secondary Fields
  transmission: {
    type: 'multiselect',
    label: 'Transmission',
    options: ['Manual', 'Automatic', 'Semi-Automatic', 'CVT']
  },
  bodyType: {
    type: 'multiselect',
    label: 'Body Type',
    options: ['Sedan', 'Hatchback', 'SUV', 'Coupe', 'Wagon', 'MPV', 'Convertible', 'Pickup']
  },
  driveType: {
    type: 'multiselect',
    label: 'Drive Type',
    options: ['FWD', 'RWD', 'AWD', '4WD']
  },
  
  // Smart Fields
  runningCostBand: {
    type: 'pills',
    label: 'Running Cost',
    options: ['Low', 'Medium', 'High'],
    icon: '💰'
  },
  insuranceEstimateBand: {
    type: 'pills',
    label: 'Insurance Estimate',
    options: ['Low', 'Medium', 'High'],
    icon: '🛡️'
  },
  verifiedSeller: {
    type: 'toggle',
    label: 'Verified Seller Only',
    icon: '✓'
  },
  inspectionAvailable: {
    type: 'toggle',
    label: 'Inspection Available',
    icon: '🔍'
  },
  notWrittenOff: {
    type: 'toggle',
    label: 'Not Written Off',
    icon: '✗'
  },
  priceRating: {
    type: 'pills',
    label: 'Price Rating',
    options: ['Great Deal', 'Good Price', 'Fair Price'],
    icon: '⭐'
  },
  
  // Advanced Fields
  engineSize: {
    type: 'range',
    label: 'Engine Size',
    unit: 'cc',
    min: 500,
    max: 8000,
    step: 100
  },
  enginePower: {
    type: 'range',
    label: 'Engine Power',
    unit: 'bhp',
    min: 50,
    max: 1000,
    step: 10
  },
  acceleration: {
    type: 'range',
    label: '0-100 km/h',
    unit: 'sec',
    min: 2,
    max: 20,
    step: 0.5
  },
  doors: {
    type: 'multiselect',
    label: 'Doors',
    options: ['2', '3', '4', '5']
  },
  seats: {
    type: 'multiselect',
    label: 'Seats',
    options: ['2', '4', '5', '6', '7', '8+']
  },
  colour: {
    type: 'multiselect',
    label: 'Colour',
    options: ['White', 'Black', 'Silver', 'Grey', 'Blue', 'Red', 'Green', 'Other']
  },
  bootSpaceBand: {
    type: 'pills',
    label: 'Boot Space',
    options: ['Small', 'Medium', 'Large']
  },
  
  // Auto Fields (read-only, calculated)
  taxEstimate: {
    type: 'auto',
    label: 'Est. Road Tax',
    unit: 'Rs/year'
  },
  co2Emissions: {
    type: 'auto',
    label: 'CO₂ Emissions',
    unit: 'g/km'
  },
  complianceStatus: {
    type: 'auto',
    label: 'Compliance',
    options: ['Compliant', 'Pending', 'Non-Compliant']
  },

  // Vehicle-specific fields
  // Vans
  payloadBand: {
    type: 'pills',
    label: 'Payload',
    options: ['Light (<500kg)', 'Medium (500-1000kg)', 'Heavy (>1000kg)']
  },
  roofHeight: {
    type: 'multiselect',
    label: 'Roof Height',
    options: ['Low', 'Medium', 'High']
  },
  wheelbase: {
    type: 'multiselect',
    label: 'Wheelbase',
    options: ['Short', 'Medium', 'Long', 'Extra Long']
  },
  cargoVolumeBand: {
    type: 'pills',
    label: 'Cargo Volume',
    options: ['Small', 'Medium', 'Large', 'Extra Large']
  },
  vatIncluded: {
    type: 'toggle',
    label: 'VAT Included',
    icon: '📄'
  },
  fleetMaintained: {
    type: 'toggle',
    label: 'Fleet Maintained',
    icon: '🔧'
  },
  
  // Motorcycles
  engineCCBand: {
    type: 'multiselect',
    label: 'Engine CC',
    options: ['Under 125cc', '125-250cc', '250-500cc', '500-1000cc', 'Over 1000cc']
  },
  bikeType: {
    type: 'multiselect',
    label: 'Bike Type',
    options: ['Sport', 'Cruiser', 'Touring', 'Adventure', 'Scooter', 'Off-road', 'Naked', 'Classic']
  },
  abs: {
    type: 'toggle',
    label: 'ABS',
    icon: '🛡️'
  },
  tractionControl: {
    type: 'toggle',
    label: 'Traction Control',
    icon: '⚙️'
  },
  
  // Electric Bikes
  brand: {
    type: 'select',
    label: 'Brand',
    placeholder: 'Any Brand',
    searchable: true
  },
  rangeBand: {
    type: 'pills',
    label: 'Range',
    options: ['<50km', '50-100km', '100-150km', '>150km']
  },
  chargeTimeBand: {
    type: 'pills',
    label: 'Charge Time',
    options: ['<3 hours', '3-5 hours', '>5 hours']
  },
  motorPowerBand: {
    type: 'pills',
    label: 'Motor Power',
    options: ['250W', '500W', '750W', '1000W+']
  },
  removableBattery: {
    type: 'toggle',
    label: 'Removable Battery',
    icon: '🔋'
  },
  roadLegal: {
    type: 'toggle',
    label: 'Road Legal',
    icon: '✓'
  },
  
  // Trucks
  grossWeightBand: {
    type: 'pills',
    label: 'Gross Weight',
    options: ['<3.5t', '3.5-7.5t', '7.5-18t', '>18t']
  },
  axleConfiguration: {
    type: 'multiselect',
    label: 'Axle Config',
    options: ['4x2', '6x2', '6x4', '8x4']
  },
  loadType: {
    type: 'multiselect',
    label: 'Load Type',
    options: ['Box', 'Flatbed', 'Curtain', 'Tipper', 'Tanker', 'Refrigerated']
  },
  maintenanceRecords: {
    type: 'toggle',
    label: 'Maintenance Records',
    icon: '📋'
  },
  
  // Farm
  equipmentType: {
    type: 'multiselect',
    label: 'Equipment Type',
    options: ['Tractor', 'Harvester', 'Sprayer', 'Plough', 'Trailer', 'Cultivator']
  },
  horsepowerBand: {
    type: 'pills',
    label: 'Horsepower',
    options: ['<50hp', '50-100hp', '100-200hp', '>200hp']
  },
  operatingHoursBand: {
    type: 'pills',
    label: 'Operating Hours',
    options: ['<1000h', '1000-3000h', '3000-5000h', '>5000h']
  },
  terrainType: {
    type: 'multiselect',
    label: 'Terrain',
    options: ['Flat', 'Hilly', 'Mixed']
  },
  
  // Boats
  boatType: {
    type: 'multiselect',
    label: 'Boat Type',
    options: ['Speedboat', 'Fishing Boat', 'Yacht', 'Jet Ski', 'Sailboat', 'Pontoon', 'Catamaran']
  },
  lengthBand: {
    type: 'pills',
    label: 'Length',
    options: ['<6m', '6-10m', '10-15m', '>15m']
  },
  hullMaterial: {
    type: 'multiselect',
    label: 'Hull Material',
    options: ['Fiberglass', 'Aluminum', 'Steel', 'Wood', 'Composite']
  },
  engineType: {
    type: 'multiselect',
    label: 'Engine Type',
    options: ['Inboard', 'Outboard', 'Sterndrive', 'Jet']
  },
  engineHoursBand: {
    type: 'pills',
    label: 'Engine Hours',
    options: ['<500h', '500-1000h', '1000-2000h', '>2000h']
  },
  surveyAvailable: {
    type: 'toggle',
    label: 'Survey Available',
    icon: '📋'
  },
  trailerIncluded: {
    type: 'toggle',
    label: 'Trailer Included',
    icon: '🚛'
  },
  
  // Three-Wheelers
  configuration: {
    type: 'multiselect',
    label: 'Configuration',
    options: ['Passenger (Taxi)', 'Cargo', 'Modified']
  },
  seatingCapacityBand: {
    type: 'pills',
    label: 'Seating',
    options: ['2', '3', '4', '6+']
  },
  permitType: {
    type: 'multiselect',
    label: 'Permit Type',
    options: ['Private', 'Taxi', 'Goods']
  },
  registrationValid: {
    type: 'toggle',
    label: 'Registration Valid',
    icon: '✓'
  },
  
  // Motorhomes & Caravans
  berths: {
    type: 'multiselect',
    label: 'Berths',
    options: ['2', '4', '6', '8+']
  },
  layoutType: {
    type: 'multiselect',
    label: 'Layout',
    options: ['Fixed Bed', 'Drop-down Bed', 'Convertible', 'Bunk']
  },
  toilet: {
    type: 'toggle',
    label: 'Toilet',
    icon: '🚽'
  },
  shower: {
    type: 'toggle',
    label: 'Shower',
    icon: '🚿'
  },
  heating: {
    type: 'toggle',
    label: 'Heating',
    icon: '🔥'
  },
  dampCheck: {
    type: 'toggle',
    label: 'Damp Check Done',
    icon: '💧'
  },
  
  // Bicycles
  bicycleType: {
    type: 'multiselect',
    label: 'Bicycle Type',
    options: ['Mountain Bike', 'Road Bike', 'Hybrid', 'BMX', 'Folding', 'Electric', 'Kids', 'City/Commuter']
  },
  frameMaterial: {
    type: 'multiselect',
    label: 'Frame Material',
    options: ['Aluminum', 'Carbon Fiber', 'Steel', 'Titanium']
  },
  wheelSize: {
    type: 'multiselect',
    label: 'Wheel Size',
    options: ['12"', '16"', '20"', '24"', '26"', '27.5"', '29"', '700c']
  },
  gears: {
    type: 'multiselect',
    label: 'Gears',
    options: ['Single Speed', '3 Speed', '7 Speed', '18 Speed', '21 Speed', '24+ Speed']
  },
  
  // Vehicle Leasing
  vehicleCategoryType: {
    type: 'multiselect',
    label: 'Vehicle Category',
    options: ['Cars', 'Vans', 'Trucks', 'Motorbikes', 'Three-Wheelers']
  },
  leaseTerm: {
    type: 'multiselect',
    label: 'Lease Term',
    options: ['1-6 Months', '6-12 Months', '12-24 Months', '24-36 Months', '36+ Months']
  },
  depositRequired: {
    type: 'pills',
    label: 'Deposit',
    options: ['No Deposit', 'Low Deposit', 'Standard Deposit']
  },
  mileageLimit: {
    type: 'pills',
    label: 'Mileage Limit',
    options: ['Unlimited', '10,000km/year', '15,000km/year', '20,000km/year']
  },
  insuranceIncluded: {
    type: 'toggle',
    label: 'Insurance Included',
    icon: '🛡️'
  },
  maintenanceIncluded: {
    type: 'toggle',
    label: 'Maintenance Included',
    icon: '🔧'
  }
};

// Filter schemas per vehicle type
export const VEHICLE_FILTER_SCHEMAS = {
  cars: {
    vehicleType: 'car',
    version: 'v1',
    filters: {
      primary: ['make', 'model', 'priceRange', 'year', 'mileage', 'location'],
      secondary: ['fuelType', 'transmission', 'bodyType', 'condition', 'driveType'],
      smart: ['runningCostBand', 'insuranceEstimateBand', 'verifiedSeller', 'inspectionAvailable', 'notWrittenOff', 'priceRating'],
      advanced: ['engineSize', 'enginePower', 'acceleration', 'doors', 'seats', 'bootSpaceBand', 'colour'],
      auto: ['taxEstimate', 'co2Emissions', 'complianceStatus']
    }
  },
  vans: {
    vehicleType: 'van',
    version: 'v1',
    filters: {
      primary: ['make', 'model', 'priceRange', 'year', 'mileage', 'location'],
      secondary: ['fuelType', 'payloadBand', 'roofHeight', 'wheelbase', 'cargoVolumeBand', 'seats'],
      smart: ['verifiedSeller', 'fleetMaintained', 'maintenanceRecords', 'vatIncluded'],
      advanced: ['engineSize', 'doors', 'colour'],
      auto: ['complianceStatus']
    }
  },
  buses: {
    vehicleType: 'bus',
    version: 'v1',
    filters: {
      primary: ['make', 'model', 'priceRange', 'year', 'mileage', 'location'],
      secondary: ['fuelType', 'seats', 'condition'],
      smart: ['verifiedSeller', 'fleetMaintained', 'maintenanceRecords'],
      advanced: ['engineSize', 'colour'],
      auto: ['complianceStatus']
    }
  },
  'motorcycles-scooters': {
    vehicleType: 'motorcycle',
    version: 'v1',
    filters: {
      primary: ['make', 'model', 'priceRange', 'year', 'location'],
      secondary: ['fuelType', 'engineCCBand', 'bikeType', 'transmission'],
      smart: ['verifiedSeller', 'inspectionAvailable'],
      advanced: ['abs', 'tractionControl', 'colour'],
      auto: []
    }
  },
  'electric-bikes': {
    vehicleType: 'electric_bike',
    version: 'v1',
    filters: {
      primary: ['brand', 'priceRange', 'condition', 'location'],
      secondary: ['rangeBand', 'chargeTimeBand', 'motorPowerBand', 'bicycleType'],
      smart: ['verifiedSeller', 'roadLegal', 'removableBattery'],
      advanced: ['colour'],
      auto: []
    }
  },
  'trucks-lorries': {
    vehicleType: 'truck',
    version: 'v1',
    filters: {
      primary: ['make', 'model', 'priceRange', 'year', 'location'],
      secondary: ['fuelType', 'grossWeightBand', 'axleConfiguration', 'loadType'],
      smart: ['maintenanceRecords', 'fleetMaintained', 'verifiedSeller'],
      advanced: ['enginePower', 'colour'],
      auto: ['complianceStatus']
    }
  },
  'farm-vehicles': {
    vehicleType: 'farm',
    version: 'v1',
    filters: {
      primary: ['equipmentType', 'brand', 'priceRange', 'year', 'location'],
      secondary: ['horsepowerBand', 'fuelType', 'operatingHoursBand'],
      smart: ['maintenanceRecords', 'verifiedSeller'],
      advanced: ['terrainType'],
      auto: []
    }
  },
  'plant-construction': {
    vehicleType: 'plant',
    version: 'v1',
    filters: {
      primary: ['equipmentType', 'brand', 'priceRange', 'year', 'location'],
      secondary: ['operatingHoursBand', 'fuelType'],
      smart: ['maintenanceRecords', 'verifiedSeller'],
      advanced: [],
      auto: []
    }
  },
  motorhomes: {
    vehicleType: 'motorhome',
    version: 'v1',
    filters: {
      primary: ['brand', 'priceRange', 'year', 'location'],
      secondary: ['berths', 'layoutType', 'fuelType', 'lengthBand'],
      smart: ['verifiedSeller', 'insuranceEstimateBand'],
      advanced: ['toilet', 'shower', 'heating'],
      auto: []
    }
  },
  caravans: {
    vehicleType: 'caravan',
    version: 'v1',
    filters: {
      primary: ['brand', 'priceRange', 'year', 'location'],
      secondary: ['berths', 'layoutType'],
      smart: ['dampCheck', 'verifiedSeller', 'maintenanceRecords'],
      advanced: ['toilet', 'shower'],
      auto: []
    }
  },
  'boats-watercraft': {
    vehicleType: 'boat',
    version: 'v1',
    filters: {
      primary: ['boatType', 'brand', 'priceRange', 'year', 'location'],
      secondary: ['lengthBand', 'hullMaterial', 'engineType', 'fuelType', 'engineHoursBand'],
      smart: ['surveyAvailable', 'verifiedSeller', 'trailerIncluded'],
      advanced: ['colour'],
      auto: []
    }
  },
  'three-wheelers': {
    vehicleType: 'three_wheeler',
    version: 'v1',
    filters: {
      primary: ['brand', 'priceRange', 'year', 'fuelType', 'location'],
      secondary: ['configuration', 'engineCCBand'],
      smart: ['verifiedSeller', 'registrationValid', 'permitType'],
      advanced: ['seatingCapacityBand', 'colour'],
      auto: []
    }
  },
  // Keep old slugs for backward compatibility
  vehicles: {
    vehicleType: 'car',
    version: 'v1',
    filters: {
      primary: ['make', 'model', 'priceRange', 'year', 'mileage', 'location'],
      secondary: ['fuelType', 'transmission', 'bodyType', 'condition', 'driveType'],
      smart: ['runningCostBand', 'insuranceEstimateBand', 'verifiedSeller', 'inspectionAvailable', 'notWrittenOff', 'priceRating'],
      advanced: ['engineSize', 'enginePower', 'acceleration', 'doors', 'seats', 'bootSpaceBand', 'colour'],
      auto: ['taxEstimate', 'co2Emissions', 'complianceStatus']
    }
  }
};

// Make options (can be fetched from API)
export const MAKE_OPTIONS = {
  cars: ['Toyota', 'Honda', 'Nissan', 'Suzuki', 'Mitsubishi', 'Mazda', 'BMW', 'Mercedes-Benz', 'Audi', 'Volkswagen', 'Hyundai', 'Kia', 'Ford', 'Chevrolet', 'Subaru', 'Lexus', 'Perodua', 'Micro'],
  vans: ['Toyota', 'Ford', 'Mercedes-Benz', 'Volkswagen', 'Renault', 'Peugeot', 'Citroen', 'Fiat', 'Nissan', 'Iveco'],
  buses: ['Tata', 'Ashok Leyland', 'Volvo', 'Mercedes-Benz', 'Scania', 'MAN', 'Isuzu', 'Hino'],
  'motorcycles-scooters': ['Honda', 'Yamaha', 'Suzuki', 'Kawasaki', 'Bajaj', 'TVS', 'Hero', 'Royal Enfield', 'KTM', 'BMW', 'Ducati', 'Harley-Davidson'],
  'electric-bikes': ['Gogoro', 'Ather', 'Ola Electric', 'TVS', 'Bajaj', 'Hero Electric', 'Okinawa', 'Ampere'],
  'trucks-lorries': ['Tata', 'Ashok Leyland', 'Mahindra', 'Eicher', 'Isuzu', 'Hino', 'Mitsubishi Fuso', 'Volvo', 'Scania', 'MAN'],
  'farm-vehicles': ['John Deere', 'Massey Ferguson', 'New Holland', 'Kubota', 'Case IH', 'TAFE', 'Mahindra', 'Sonalika'],
  'plant-construction': ['Caterpillar', 'JCB', 'Komatsu', 'Hitachi', 'Volvo', 'Liebherr', 'Doosan', 'Kobelco'],
  motorhomes: ['Winnebago', 'Thor', 'Coachmen', 'Forest River', 'Jayco', 'Airstream', 'Tiffin'],
  caravans: ['Jayco', 'Bailey', 'Swift', 'Coachman', 'Lunar', 'Elddis', 'Adria'],
  'boats-watercraft': ['Yamaha', 'Mercury', 'Honda', 'Suzuki', 'Sea-Doo', 'Bayliner', 'Boston Whaler', 'Chaparral'],
  'three-wheelers': ['Bajaj', 'Piaggio', 'TVS', 'Mahindra', 'Atul', 'Lohia'],
  vehicles: ['Toyota', 'Honda', 'Nissan', 'Suzuki', 'Mitsubishi', 'Mazda', 'BMW', 'Mercedes-Benz', 'Audi', 'Volkswagen']
};

// Helper function to check if a category is a vehicle category
export const isVehicleCategory = (slug) => {
  const vehicleSlugs = [
    'vehicles',
    'cars',
    'vans',
    'buses',
    'motorcycles-scooters',
    'electric-bikes',
    'trucks-lorries',
    'farm-vehicles',
    'plant-construction',
    'motorhomes',
    'caravans',
    'boats-watercraft',
    'three-wheelers'
  ];
  return vehicleSlugs.includes(slug?.toLowerCase());
};

// Helper function to get the schema for a vehicle type
export const getVehicleSchema = (vehicleType) => {
  return VEHICLE_FILTER_SCHEMAS[vehicleType] || VEHICLE_FILTER_SCHEMAS.cars;
};

// Helper function to get field definition
export const getFieldDef = (fieldName) => {
  return FIELD_DEFINITIONS[fieldName] || { type: 'text', label: fieldName };
};

export default {
  VEHICLE_TYPES,
  FIELD_DEFINITIONS,
  VEHICLE_FILTER_SCHEMAS,
  MAKE_OPTIONS,
  isVehicleCategory,
  getVehicleSchema,
  getFieldDef
};
